import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import {
  Plus, Trash2, Edit2, X, Check, GripVertical,
  ChevronDown, ChevronUp, HelpCircle, FolderPlus,
  Eye, EyeOff, Globe, GlobeLock, Upload, Undo2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const tempId = () => `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const FAQManagementTab = ({ authHeaders, flash, onUnsavedChanges }) => {
  // Server state (source of truth from DB)
  const [serverCats, setServerCats] = useState([]);
  const [serverItems, setServerItems] = useState([]);
  // Local working state (edited by user, not yet saved)
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [initialLoad, setInitialLoad] = useState(true);
  const [saving, setSaving] = useState(false);
  // UI states
  const [editingCat, setEditingCat] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [newCat, setNewCat] = useState(null);
  const [newItem, setNewItem] = useState(null);
  const [expandedCats, setExpandedCats] = useState({});
  const changeCount = useRef(0);
  const [dirty, setDirty] = useState(false);

  const markDirty = useCallback(() => {
    changeCount.current += 1;
    setDirty(true);
  }, []);

  const fetchAll = useCallback(async (resetDirty = true) => {
    try {
      const [catRes, itemRes] = await Promise.all([
        axios.get(`${API}/api/portal/admin/faq/categories`, { headers: authHeaders() }),
        axios.get(`${API}/api/portal/admin/faq/items`, { headers: authHeaders() }),
      ]);
      const cats = catRes.data;
      const itms = itemRes.data;
      setServerCats(JSON.parse(JSON.stringify(cats)));
      setServerItems(JSON.parse(JSON.stringify(itms)));
      setCategories(cats);
      setItems(itms);
      setExpandedCats(prev => {
        if (Object.keys(prev).length === 0) {
          const expanded = {};
          cats.forEach(c => { expanded[c.id] = true; });
          return expanded;
        }
        return prev;
      });
      if (resetDirty) {
        changeCount.current = 0;
        setDirty(false);
      }
    } catch {
      flash('Failed to load FAQ data', true);
    }
    setInitialLoad(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only fetch on mount
  const hasFetched = useRef(false);
  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchAll(true);
    }
  }, [fetchAll]);

  // Notify parent about unsaved changes (for navigation guard)
  useEffect(() => {
    if (onUnsavedChanges) onUnsavedChanges(dirty);
  }, [dirty, onUnsavedChanges]);

  // Browser tab close / refresh guard
  useEffect(() => {
    const handler = (e) => {
      if (dirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const getItemsForCategory = (catId) =>
    items.filter(it => it.category_id === catId).sort((a, b) => a.order - b.order);

  // ═══════════════ LOCAL-ONLY MUTATIONS (no API calls) ═══════════════

  const toggleItemStatus = (itemId, currentStatus) => {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    setItems(prev => prev.map(it => it.id === itemId ? { ...it, status: newStatus } : it));
    markDirty();
  };

  const toggleCategoryStatus = (catId, currentStatus) => {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    setCategories(prev => prev.map(c => c.id === catId ? { ...c, status: newStatus } : c));
    setItems(prev => prev.map(it => it.category_id === catId ? { ...it, status: newStatus } : it));
    markDirty();
  };

  const bulkSetStatus = (status) => {
    const label = status === 'published' ? 'Publish' : 'Draft';
    if (!window.confirm(`${label} ALL FAQ categories and questions?`)) return;
    setCategories(prev => prev.map(c => ({ ...c, status })));
    setItems(prev => prev.map(it => ({ ...it, status })));
    markDirty();
  };

  const addCategory = () => {
    if (!newCat.name_en.trim()) { flash('English name required', true); return; }
    const maxOrder = categories.length > 0 ? Math.max(...categories.map(c => c.order || 0)) + 1 : 0;
    setCategories(prev => [...prev, { id: tempId(), name_en: newCat.name_en, name_fr: newCat.name_fr || '', order: maxOrder, status: 'published' }]);
    setNewCat(null);
    markDirty();
  };

  const saveEditCategory = () => {
    setCategories(prev => prev.map(c => c.id === editingCat.id ? { ...c, name_en: editingCat.name_en, name_fr: editingCat.name_fr } : c));
    setEditingCat(null);
    markDirty();
  };

  const deleteCategory = (catId) => {
    const count = getItemsForCategory(catId).length;
    if (!window.confirm(`Delete this category and its ${count} question(s)?`)) return;
    setCategories(prev => prev.filter(c => c.id !== catId));
    setItems(prev => prev.filter(it => it.category_id !== catId));
    markDirty();
  };

  const moveCategoryUp = (catId) => {
    setCategories(prev => {
      const sorted = [...prev].sort((a, b) => a.order - b.order);
      const idx = sorted.findIndex(c => c.id === catId);
      if (idx <= 0) return prev;
      [sorted[idx - 1], sorted[idx]] = [sorted[idx], sorted[idx - 1]];
      return sorted.map((c, i) => ({ ...c, order: i }));
    });
    markDirty();
  };

  const moveCategoryDown = (catId) => {
    setCategories(prev => {
      const sorted = [...prev].sort((a, b) => a.order - b.order);
      const idx = sorted.findIndex(c => c.id === catId);
      if (idx >= sorted.length - 1) return prev;
      [sorted[idx], sorted[idx + 1]] = [sorted[idx + 1], sorted[idx]];
      return sorted.map((c, i) => ({ ...c, order: i }));
    });
    markDirty();
  };

  const addItem = () => {
    if (!newItem.question_en.trim()) { flash('English question required', true); return; }
    const catItems = items.filter(it => it.category_id === newItem.category_id);
    const maxOrder = catItems.length > 0 ? Math.max(...catItems.map(it => it.order || 0)) + 1 : 0;
    setItems(prev => [...prev, {
      id: tempId(), category_id: newItem.category_id,
      question_en: newItem.question_en, question_fr: newItem.question_fr || '',
      answer_en: newItem.answer_en || '', answer_fr: newItem.answer_fr || '',
      order: maxOrder, status: 'published',
    }]);
    setNewItem(null);
    markDirty();
  };

  const saveEditItem = () => {
    setItems(prev => prev.map(it => it.id === editingItem.id ? { ...it, ...editingItem } : it));
    setEditingItem(null);
    markDirty();
  };

  const deleteItem = (itemId) => {
    if (!window.confirm('Delete this question?')) return;
    setItems(prev => prev.filter(it => it.id !== itemId));
    markDirty();
  };

  const moveItemUp = (catId, itemId) => {
    setItems(prev => {
      const catItems = prev.filter(it => it.category_id === catId).sort((a, b) => a.order - b.order);
      const others = prev.filter(it => it.category_id !== catId);
      const idx = catItems.findIndex(it => it.id === itemId);
      if (idx <= 0) return prev;
      [catItems[idx - 1], catItems[idx]] = [catItems[idx], catItems[idx - 1]];
      return [...others, ...catItems.map((it, i) => ({ ...it, order: i }))];
    });
    markDirty();
  };

  const moveItemDown = (catId, itemId) => {
    setItems(prev => {
      const catItems = prev.filter(it => it.category_id === catId).sort((a, b) => a.order - b.order);
      const others = prev.filter(it => it.category_id !== catId);
      const idx = catItems.findIndex(it => it.id === itemId);
      if (idx >= catItems.length - 1) return prev;
      [catItems[idx], catItems[idx + 1]] = [catItems[idx + 1], catItems[idx]];
      return [...others, ...catItems.map((it, i) => ({ ...it, order: i }))];
    });
    markDirty();
  };

  const moveItemToCategory = (itemId, newCatId) => {
    setItems(prev => prev.map(it => it.id === itemId ? { ...it, category_id: newCatId } : it));
    markDirty();
  };

  // ═══════════════ CONFIRM / DISCARD ═══════════════

  const confirmChanges = async () => {
    setSaving(true);
    const headers = authHeaders();
    let errors = 0;
    try {
      // 1) Delete removed categories (and their items cascade on server)
      const deletedCats = serverCats.filter(sc => !categories.find(c => c.id === sc.id));
      for (const dc of deletedCats) {
        await axios.delete(`${API}/api/portal/admin/faq/categories/${dc.id}`, { headers });
      }

      // 2) Delete removed items
      const deletedItems = serverItems.filter(si => !items.find(i => i.id === si.id) && categories.find(c => c.id === si.category_id));
      for (const di of deletedItems) {
        await axios.delete(`${API}/api/portal/admin/faq/items/${di.id}`, { headers });
      }

      // 3) Create new categories (temp IDs)
      const catIdMap = {}; // temp-id → real-id
      for (const cat of categories) {
        if (cat.id.startsWith('temp-')) {
          const res = await axios.post(`${API}/api/portal/admin/faq/categories`, {
            name_en: cat.name_en, name_fr: cat.name_fr, status: cat.status,
          }, { headers });
          catIdMap[cat.id] = res.data.id;
        }
      }

      // 4) Update existing categories (explicit field comparison)
      for (const cat of categories) {
        if (!cat.id.startsWith('temp-')) {
          const server = serverCats.find(sc => sc.id === cat.id);
          const changed = !server ||
            server.name_en !== cat.name_en ||
            server.name_fr !== cat.name_fr ||
            server.order !== cat.order ||
            (server.status || 'published') !== (cat.status || 'published');
          if (changed) {
            try {
              await axios.put(`${API}/api/portal/admin/faq/categories/${cat.id}`, {
                name_en: cat.name_en, name_fr: cat.name_fr, order: cat.order, status: cat.status,
              }, { headers });
            } catch { errors++; }
          }
        }
      }

      // 5) Reorder categories (avoid mutating state — use spread before sort)
      const sortedCats = [...categories].sort((a, b) => a.order - b.order);
      const catIds = sortedCats.map(c => catIdMap[c.id] || c.id);
      await axios.post(`${API}/api/portal/admin/faq/categories/reorder`, { ids: catIds }, { headers });

      // 6) Create new items
      const itemIdMap = {};
      for (const item of items) {
        if (item.id.startsWith('temp-')) {
          const realCatId = catIdMap[item.category_id] || item.category_id;
          const res = await axios.post(`${API}/api/portal/admin/faq/items`, {
            category_id: realCatId, question_en: item.question_en, question_fr: item.question_fr,
            answer_en: item.answer_en, answer_fr: item.answer_fr, status: item.status,
          }, { headers });
          itemIdMap[item.id] = res.data.id;
        }
      }

      // 7) Update existing items (explicit field comparison, not JSON.stringify)
      for (const item of items) {
        if (!item.id.startsWith('temp-')) {
          const server = serverItems.find(si => si.id === item.id);
          const changed = !server ||
            server.category_id !== item.category_id ||
            server.question_en !== item.question_en ||
            server.question_fr !== item.question_fr ||
            server.answer_en !== item.answer_en ||
            server.answer_fr !== item.answer_fr ||
            server.order !== item.order ||
            (server.status || 'published') !== (item.status || 'published');
          if (changed) {
            try {
              await axios.put(`${API}/api/portal/admin/faq/items/${item.id}`, {
                category_id: item.category_id, question_en: item.question_en, question_fr: item.question_fr,
                answer_en: item.answer_en, answer_fr: item.answer_fr, order: item.order, status: item.status,
              }, { headers });
            } catch { errors++; }
          }
        }
      }

      // 8) Reorder items per category
      const catGroups = {};
      for (const item of items) {
        const realCatId = catIdMap[item.category_id] || item.category_id;
        if (!catGroups[realCatId]) catGroups[realCatId] = [];
        catGroups[realCatId].push(itemIdMap[item.id] || item.id);
      }
      for (const ids of Object.values(catGroups)) {
        await axios.post(`${API}/api/portal/admin/faq/items/reorder`, { ids }, { headers });
      }

      if (errors > 0) {
        flash(`Published with ${errors} warning(s). Some items may need re-saving.`, true);
      } else {
        flash('All changes published to website');
      }
      await fetchAll();
    } catch (err) {
      flash('Failed to save some changes. Please try again.', true);
      await fetchAll();
    }
    setSaving(false);
  };

  const discardChanges = () => {
    if (!window.confirm('Discard all unsaved changes?')) return;
    setCategories(JSON.parse(JSON.stringify(serverCats)));
    setItems(JSON.parse(JSON.stringify(serverItems)));
    setEditingCat(null);
    setEditingItem(null);
    setNewCat(null);
    setNewItem(null);
    changeCount.current = 0;
    setDirty(false);
    flash('Changes discarded');
  };

  const inputCls = "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-500";
  const textareaCls = "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-500 resize-y min-h-[60px]";

  const publishedCats = categories.filter(c => c.status !== 'draft').length;
  const draftCats = categories.filter(c => c.status === 'draft').length;
  const publishedItems = items.filter(i => i.status !== 'draft').length;
  const draftItems = items.filter(i => i.status === 'draft').length;

  if (initialLoad) return (
    <div className="flex items-center justify-center py-12">
      <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div data-testid="faq-management">
      {/* Header + CONFIRM EDIT button */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-black text-gray-900" data-testid="faq-mgmt-heading">FAQ Management</h3>
          <p className="text-xs text-gray-500 mt-0.5">Manage the FAQ section on the Why Total Composites page</p>
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <>
              <button
                onClick={discardChanges}
                className="inline-flex items-center gap-2 px-4 py-2 font-bold text-sm border border-gray-300 text-gray-500 rounded-lg hover:border-red-400 hover:text-red-600 transition-all"
                data-testid="discard-changes-btn"
              >
                <Undo2 size={14} /> DISCARD
              </button>
              <button
                onClick={confirmChanges}
                disabled={saving}
                className="inline-flex items-center gap-2 px-5 py-2 font-bold text-sm text-white rounded-lg transition-all shadow-lg hover:shadow-xl disabled:opacity-60"
                style={{ backgroundColor: '#E8520A' }}
                data-testid="confirm-edit-btn"
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Upload size={14} />
                )}
                {saving ? 'PUBLISHING...' : 'CONFIRM EDIT'}
                {!saving && (
                  <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-white/20 rounded">{changeCount.current}</span>
                )}
              </button>
            </>
          )}
          <button
            onClick={() => setNewCat({ name_en: '', name_fr: '' })}
            className="inline-flex items-center gap-2 px-4 py-2 font-bold text-sm border border-gray-300 text-gray-700 rounded-lg hover:border-orange-500 hover:text-orange-600 transition-all"
            data-testid="add-category-btn"
          >
            <FolderPlus size={14} /> ADD CATEGORY
          </button>
        </div>
      </div>

      {/* Unsaved changes banner */}
      {dirty && (
        <div className="flex items-center gap-3 mb-4 px-4 py-2.5 bg-orange-50 border border-orange-200 rounded-lg" data-testid="unsaved-banner">
          <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
          <p className="text-xs font-bold text-orange-700">
            {changeCount.current} unsaved change{changeCount.current !== 1 ? 's' : ''} — click <span className="uppercase">Confirm Edit</span> to publish to website
          </p>
        </div>
      )}

      {/* Bulk Actions Bar */}
      <div className="flex items-center gap-3 mb-5 p-3 bg-gray-50 rounded-xl border border-gray-100" data-testid="faq-bulk-actions">
        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider mr-1">Bulk:</span>
        <button
          onClick={() => bulkSetStatus('published')}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors"
          data-testid="publish-all-btn"
        >
          <Globe size={12} /> Publish All
        </button>
        <button
          onClick={() => bulkSetStatus('draft')}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors"
          data-testid="draft-all-btn"
        >
          <GlobeLock size={12} /> Draft All
        </button>
        <div className="ml-auto flex items-center gap-3 text-[11px] text-gray-400">
          <span className="inline-flex items-center gap-1"><Eye size={11} className="text-emerald-500" /> {publishedCats} sections / {publishedItems} questions live</span>
          {draftItems > 0 && <span className="inline-flex items-center gap-1"><EyeOff size={11} className="text-amber-500" /> {draftCats > 0 ? `${draftCats} sections / ` : ''}{draftItems} questions drafted</span>}
        </div>
      </div>

      {/* New Category Form */}
      {newCat && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-5" data-testid="new-cat-form">
          <h4 className="text-sm font-bold text-gray-900 mb-3">New Category</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <input className={inputCls} placeholder="Category name (English) *" value={newCat.name_en} onChange={e => setNewCat(p => ({ ...p, name_en: e.target.value }))} data-testid="new-cat-name-en" />
            <input className={inputCls} placeholder="Nom de catégorie (Français)" value={newCat.name_fr} onChange={e => setNewCat(p => ({ ...p, name_fr: e.target.value }))} data-testid="new-cat-name-fr" />
          </div>
          <div className="flex gap-2">
            <button onClick={addCategory} className="px-4 py-2 font-bold text-white rounded-lg text-sm" style={{ backgroundColor: '#E8520A' }} data-testid="save-new-cat">CREATE</button>
            <button onClick={() => setNewCat(null)} className="px-4 py-2 font-bold text-gray-600 border border-gray-300 rounded-lg text-sm">CANCEL</button>
          </div>
        </div>
      )}

      {/* Categories + Items */}
      <div className="space-y-4" data-testid="faq-categories-list">
        {[...categories].sort((a, b) => a.order - b.order).map((cat, catIdx) => {
          const catItems = getItemsForCategory(cat.id);
          const isExpanded = expandedCats[cat.id];
          const isDraftCat = cat.status === 'draft';
          const draftCount = catItems.filter(i => i.status === 'draft').length;

          return (
            <div
              key={cat.id}
              className={`border rounded-xl overflow-hidden transition-opacity ${isDraftCat ? 'border-amber-300 bg-amber-50/30 opacity-75' : 'border-gray-200'}`}
              data-testid={`faq-cat-${cat.id}`}
            >
              {/* Category Header */}
              <div className={`px-4 py-3 flex items-center justify-between ${isDraftCat ? 'bg-amber-50' : 'bg-gray-50'}`}>
                {editingCat?.id === cat.id ? (
                  <div className="flex-1 flex items-center gap-2">
                    <input className={inputCls + ' max-w-[200px]'} value={editingCat.name_en} onChange={e => setEditingCat(p => ({ ...p, name_en: e.target.value }))} data-testid="edit-cat-name-en" />
                    <input className={inputCls + ' max-w-[200px]'} value={editingCat.name_fr} onChange={e => setEditingCat(p => ({ ...p, name_fr: e.target.value }))} placeholder="Français" data-testid="edit-cat-name-fr" />
                    <button onClick={saveEditCategory} className="p-1.5 text-green-600 hover:bg-green-50 rounded" data-testid="save-edit-cat"><Check size={14} /></button>
                    <button onClick={() => setEditingCat(null)} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded"><X size={14} /></button>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => setExpandedCats(p => ({ ...p, [cat.id]: !p[cat.id] }))}
                      className="flex items-center gap-2 flex-1 text-left"
                      data-testid={`toggle-cat-${cat.id}`}
                    >
                      {isExpanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
                      <span className={`text-sm font-bold ${isDraftCat ? 'text-amber-700' : 'text-gray-900'}`}>{cat.name_en}</span>
                      {cat.name_fr && <span className="text-xs text-gray-400">/ {cat.name_fr}</span>}
                      <span className="text-[10px] font-bold text-gray-400 bg-gray-200 rounded-full px-2 py-0.5 ml-1">{catItems.length}</span>
                      {isDraftCat && (
                        <span className="text-[10px] font-bold text-amber-600 bg-amber-100 rounded-full px-2 py-0.5 ml-1">DRAFT</span>
                      )}
                      {!isDraftCat && draftCount > 0 && (
                        <span className="text-[10px] font-bold text-amber-500 bg-amber-50 rounded-full px-2 py-0.5 ml-1">{draftCount} drafted</span>
                      )}
                    </button>
                    <div className="flex items-center gap-0.5">
                      <button
                        onClick={() => toggleCategoryStatus(cat.id, cat.status || 'published')}
                        className={`p-1.5 rounded transition-colors ${isDraftCat ? 'text-amber-500 hover:text-emerald-600 hover:bg-emerald-50' : 'text-emerald-500 hover:text-amber-600 hover:bg-amber-50'}`}
                        title={isDraftCat ? 'Publish section' : 'Draft section'}
                        data-testid={`toggle-cat-status-${cat.id}`}
                      >
                        {isDraftCat ? <EyeOff size={14} className="pointer-events-none" /> : <Eye size={14} className="pointer-events-none" />}
                      </button>
                      <button onClick={() => moveCategoryUp(cat.id)} disabled={catIdx === 0} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-25" title="Move up"><ChevronUp size={14} /></button>
                      <button onClick={() => moveCategoryDown(cat.id)} disabled={catIdx === categories.length - 1} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-25" title="Move down"><ChevronDown size={14} /></button>
                      <button onClick={() => setEditingCat({ ...cat })} className="p-1 text-gray-400 hover:text-orange-600" data-testid={`edit-cat-btn-${cat.id}`}><Edit2 size={13} /></button>
                      <button onClick={() => deleteCategory(cat.id)} className="p-1 text-gray-400 hover:text-red-600" data-testid={`delete-cat-btn-${cat.id}`}><Trash2 size={13} /></button>
                    </div>
                  </>
                )}
              </div>

              {/* Items */}
              {isExpanded && (
                <div className="divide-y divide-gray-100">
                  {catItems.map((item, itemIdx) => {
                    const isDraftItem = item.status === 'draft';
                    return (
                      <div key={item.id} className={`px-4 py-3 transition-opacity ${isDraftItem ? 'opacity-60 bg-amber-50/40' : ''}`} data-testid={`faq-item-${item.id}`}>
                        {editingItem?.id === item.id ? (
                          <div className="space-y-2">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              <input className={inputCls} placeholder="Question (English) *" value={editingItem.question_en} onChange={e => setEditingItem(p => ({ ...p, question_en: e.target.value }))} data-testid="edit-item-q-en" />
                              <input className={inputCls} placeholder="Question (Français)" value={editingItem.question_fr} onChange={e => setEditingItem(p => ({ ...p, question_fr: e.target.value }))} data-testid="edit-item-q-fr" />
                            </div>
                            <textarea className={textareaCls} rows={3} placeholder="Answer (English) *" value={editingItem.answer_en} onChange={e => setEditingItem(p => ({ ...p, answer_en: e.target.value }))} data-testid="edit-item-a-en" />
                            <textarea className={textareaCls} rows={3} placeholder="Réponse (Français)" value={editingItem.answer_fr} onChange={e => setEditingItem(p => ({ ...p, answer_fr: e.target.value }))} data-testid="edit-item-a-fr" />
                            <div className="flex items-center gap-2">
                              <select className={inputCls + ' max-w-[200px]'} value={editingItem.category_id} onChange={e => setEditingItem(p => ({ ...p, category_id: e.target.value }))} data-testid="edit-item-cat">
                                {categories.map(c => <option key={c.id} value={c.id}>{c.name_en}</option>)}
                              </select>
                              <button onClick={saveEditItem} className="px-3 py-1.5 text-xs font-bold text-white rounded" style={{ backgroundColor: '#E8520A' }} data-testid="save-edit-item">Save</button>
                              <button onClick={() => setEditingItem(null)} className="px-3 py-1.5 text-xs font-bold text-gray-600 border rounded">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start gap-3">
                            <div className="flex flex-col items-center gap-0.5 pt-0.5">
                              <button onClick={() => moveItemUp(cat.id, item.id)} disabled={itemIdx === 0} className="text-gray-300 hover:text-gray-500 disabled:opacity-25"><ChevronUp size={12} /></button>
                              <GripVertical size={12} className="text-gray-300" />
                              <button onClick={() => moveItemDown(cat.id, item.id)} disabled={itemIdx === catItems.length - 1} className="text-gray-300 hover:text-gray-500 disabled:opacity-25"><ChevronDown size={12} /></button>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className={`text-sm font-bold leading-snug ${isDraftItem ? 'text-amber-700' : 'text-gray-900'}`}>{item.question_en}</p>
                                {isDraftItem && (
                                  <span className="text-[9px] font-bold text-amber-600 bg-amber-100 rounded px-1.5 py-0.5 flex-shrink-0">DRAFT</span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 mt-1 line-clamp-2">{item.answer_en}</p>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button
                                onClick={() => toggleItemStatus(item.id, item.status || 'published')}
                                className={`p-1 rounded transition-colors ${isDraftItem ? 'text-amber-500 hover:text-emerald-600 hover:bg-emerald-50' : 'text-emerald-500 hover:text-amber-500 hover:bg-amber-50'}`}
                                title={isDraftItem ? 'Publish question' : 'Draft question'}
                                data-testid={`toggle-item-status-${item.id}`}
                              >
                                {isDraftItem ? <EyeOff size={13} className="pointer-events-none" /> : <Eye size={13} className="pointer-events-none" />}
                              </button>
                              <select
                                className="text-[10px] border border-gray-200 rounded px-1 py-0.5 text-gray-400 bg-white"
                                value={item.category_id}
                                onChange={e => moveItemToCategory(item.id, e.target.value)}
                                title="Move to category"
                                data-testid={`move-item-${item.id}`}
                              >
                                {categories.map(c => <option key={c.id} value={c.id}>{c.name_en}</option>)}
                              </select>
                              <button onClick={() => setEditingItem({ ...item })} className="p-1 text-gray-400 hover:text-orange-600" data-testid={`edit-item-btn-${item.id}`}><Edit2 size={13} /></button>
                              <button onClick={() => deleteItem(item.id)} className="p-1 text-gray-400 hover:text-red-600" data-testid={`delete-item-btn-${item.id}`}><Trash2 size={13} /></button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {newItem?.category_id === cat.id ? (
                    <div className="px-4 py-3 bg-blue-50" data-testid="new-item-form">
                      <div className="space-y-2">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <input className={inputCls} placeholder="Question (English) *" value={newItem.question_en} onChange={e => setNewItem(p => ({ ...p, question_en: e.target.value }))} data-testid="new-item-q-en" />
                          <input className={inputCls} placeholder="Question (Français)" value={newItem.question_fr} onChange={e => setNewItem(p => ({ ...p, question_fr: e.target.value }))} data-testid="new-item-q-fr" />
                        </div>
                        <textarea className={textareaCls} rows={3} placeholder="Answer (English) *" value={newItem.answer_en} onChange={e => setNewItem(p => ({ ...p, answer_en: e.target.value }))} data-testid="new-item-a-en" />
                        <textarea className={textareaCls} rows={3} placeholder="Réponse (Français)" value={newItem.answer_fr} onChange={e => setNewItem(p => ({ ...p, answer_fr: e.target.value }))} data-testid="new-item-a-fr" />
                        <div className="flex gap-2">
                          <button onClick={addItem} className="px-3 py-1.5 text-xs font-bold text-white rounded" style={{ backgroundColor: '#E8520A' }} data-testid="save-new-item">ADD QUESTION</button>
                          <button onClick={() => setNewItem(null)} className="px-3 py-1.5 text-xs font-bold text-gray-600 border rounded">Cancel</button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setNewItem({ category_id: cat.id, question_en: '', question_fr: '', answer_en: '', answer_fr: '' })}
                      className="w-full px-4 py-2.5 text-xs font-bold text-gray-400 hover:text-orange-600 hover:bg-orange-50 transition-colors flex items-center justify-center gap-1.5"
                      data-testid={`add-item-btn-${cat.id}`}
                    >
                      <Plus size={12} /> Add Question
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {categories.length === 0 && (
          <div className="text-center py-12 border border-dashed border-gray-300 rounded-lg">
            <HelpCircle size={32} className="mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-gray-500">No FAQ categories yet</p>
            <p className="text-xs text-gray-400 mt-1">Create categories and add questions to build your FAQ page</p>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="mt-6 pt-4 border-t border-gray-100 flex items-center gap-6 text-xs text-gray-400">
        <span>{categories.length} categories</span>
        <span>{items.length} questions</span>
        <span className="inline-flex items-center gap-1"><Eye size={10} className="text-emerald-500" /> {publishedItems} published</span>
        {draftItems > 0 && <span className="inline-flex items-center gap-1"><EyeOff size={10} className="text-amber-500" /> {draftItems} drafted</span>}
      </div>
    </div>
  );
};

export default FAQManagementTab;
